import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {getUpdatedCellWithLSUV} from 'notebook/structured-tables/row-updaters/getUpdatedCellWithLSUV';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES} from 'notebook/tables/refresh-table-data/updateStructuredTableUsingRowUpdater';
import {Cell, TypedValue} from 'notebook/tables/TableConstants';

/**
 * Builds an updated cells array by applying the standard LSUV-preservation + live-value update
 * pattern shared across most structured table row updaters.
 *
 * For columns in COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES (e.g. CUSTOM), the cell value
 * is restored from the existing lastSyncedUpstreamValues snapshot rather than overwritten by the
 * live entity value.  For all other columns, updateCellFn is called to get the live value and
 * updateCellWithLiveValue writes it into the cell while preserving formatting.
 *
 * Callers are responsible for performing any inaccessible / archive checks before calling this
 * helper.
 */
export function buildStandardUpdatedCells<TModel>(
  model: TModel,
  columns: Array<StructuredTableColumn>,
  row: StructuredTableRow<unknown>,
  columnGroup: ColumnGroup | null,
  updateCellFn: (
    model: TModel,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ) => LiveCellValue,
  options?: {setRawTextValue?: (v: TypedValue) => string}
): Array<Cell> {
  return row.cells.map((cell, columnIndex) => {
    const column = columns[columnIndex];
    if (COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES.includes(column.columnType)) {
      return getUpdatedCellWithLSUV(column.provenanceUuid, cell, row.lastSyncedUpstreamValues);
    }
    return updateCellWithLiveValue(cell, updateCellFn(model, columnGroup, column), options);
  });
}
